import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    const validateToken = async () => {
      if (!isMounted) return;
      setIsValidating(true);

      try {
        // Get token from URL or state
        const token = searchParams.get("access_token") || 
                     searchParams.get("token") || 
                     location.state?.token;
        const type = searchParams.get("type") || location.state?.type;

        console.log("Token and type:", { token, type });

        if (!token || !type) {
          console.error("Missing token or type:", { token, type });
          toast.error("Le lien de réinitialisation est invalide ou a expiré");
          setTimeout(() => navigate("/login-owner"), 2000);
          return;
        }

        if (type !== "recovery") {
          console.error("Invalid token type:", type);
          toast.error("Le type de lien est invalide");
          setTimeout(() => navigate("/login-owner"), 2000);
          return;
        }

        // Verify access token with Supabase
        const { data: { user }, error } = await supabase.auth.getUser(token);

        if (error || !user) {
          console.error("Token validation error:", error);
          toast.error("Le lien de réinitialisation a expiré ou est invalide");
          setTimeout(() => navigate("/login-owner"), 2000);
          return;
        }

        if (isMounted) {
          console.log("Token validation successful for user:", user);
          setIsTokenValid(true);
        }
      } catch (error) {
        console.error("Token validation error:", error);
        toast.error("Une erreur s'est produite lors de la vérification du lien");
        setTimeout(() => navigate("/login-owner"), 2000);
      } finally {
        if (isMounted) {
          setIsValidating(false);
        }
      }
    };

    validateToken();

    return () => {
      isMounted = false;
    };
  }, [searchParams, navigate, location.state]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setIsResetting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success("Mot de passe modifié avec succès");
      setTimeout(() => navigate("/login-owner"), 2000);
    } catch (error: any) {
      console.error("Error resetting password:", error);
      toast.error(error.message || "Erreur lors de la modification du mot de passe");
    } finally {
      setIsResetting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Vérification du lien...</h2>
          <p className="text-gray-600">Veuillez patienter pendant la vérification du lien de réinitialisation.</p>
        </div>
      </div>
    );
  }

  if (!isTokenValid) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Lien invalide</h2>
          <p className="text-gray-600 mb-4">Le lien de réinitialisation est invalide ou a expiré.</p>
          <Button onClick={() => navigate("/login-owner")}>
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="text-center mb-8">
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
              Réinitialisation du mot de passe
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Définissez votre nouveau mot de passe
            </p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nouveau mot de passe</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Entrez le nouveau mot de passe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmez le nouveau mot de passe"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isResetting}
            >
              {isResetting ? "Modification en cours..." : "Modifier le mot de passe"}
            </Button>
          </form>
        </div>
      </div>
      <div className="relative hidden w-0 flex-1 lg:block">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1553413077-190dd305871c"
          alt="Entrepôt moderne avec des étagères de stockage bien organisées"
        />
      </div>
    </div>
  );
}
